import { useCallback, useEffect, useRef, useState } from "react";

import {
  createSupportIssue,
  createSupportIssueMessage,
  getSupportIssue,
  getSupportUnreadCount,
  listSupportCompanies,
  listSupportIssues,
  markSupportIssueRead,
  updateSupportIssueStatus,
} from "../../services/supportIssues";

const mergeById = (current, incoming, prepend = false) => {
  const ordered = prepend ? [...incoming, ...current] : [...current, ...incoming];
  const seen = new Set();

  return ordered.filter((entry) => {
    if (!entry || seen.has(entry.id)) return false;
    seen.add(entry.id);
    return true;
  });
};

const initialAsyncState = { status: "idle", error: null };

export const useSupportIssues = ({ companyId, userId, socketManager }) => {
  const [issues, setIssues] = useState([]);
  const [issuesCursor, setIssuesCursor] = useState(null);
  const [listState, setListState] = useState(initialAsyncState);
  const [companies, setCompanies] = useState([]);
  const [companiesCursor, setCompaniesCursor] = useState(null);
  const [companiesState, setCompaniesState] = useState(initialAsyncState);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [detailState, setDetailState] = useState(initialAsyncState);
  const [unreadCount, setUnreadCount] = useState(0);

  const currentIdentity = `${companyId || ""}:${userId || ""}`;
  const identityRef = useRef(currentIdentity);
  const generationRef = useRef(0);
  const selectedIssueIdRef = useRef(null);
  const filtersRef = useRef({ limit: 20 });
  const hasLoadedListRef = useRef(false);
  const listSequenceRef = useRef(0);
  const listForegroundRef = useRef(null);
  const companiesSequenceRef = useRef(0);
  const detailSequenceRef = useRef(0);
  const detailForegroundRef = useRef(null);
  const unreadSequenceRef = useRef(0);
  const createBusyRef = useRef(null);
  const replyBusyRef = useRef(new Map());
  const statusBusyRef = useRef(new Map());
  const readQueueRef = useRef(null);
  const selectedIssueRef = useRef(null);
  const identityChanged = identityRef.current !== currentIdentity;

  // Invalidate previous-login requests during render. Waiting for an effect would
  // leave a small window where a stale response could populate the new identity.
  if (identityChanged) {
    identityRef.current = currentIdentity;
    generationRef.current += 1;
    selectedIssueIdRef.current = null;
    filtersRef.current = { limit: 20 };
    hasLoadedListRef.current = false;
    listSequenceRef.current += 1;
    listForegroundRef.current = null;
    companiesSequenceRef.current += 1;
    detailSequenceRef.current += 1;
    detailForegroundRef.current = null;
    unreadSequenceRef.current += 1;
    createBusyRef.current = null;
    replyBusyRef.current.clear();
    statusBusyRef.current.clear();
    readQueueRef.current = null;
    selectedIssueRef.current = null;
  }

  useEffect(() => {
    selectedIssueRef.current = selectedIssue;
    if (selectedIssue) selectedIssueIdRef.current = selectedIssue.id;
  }, [selectedIssue]);

  const isIdentityCurrent = useCallback((generation) => (
    generation === generationRef.current &&
    identityRef.current === currentIdentity
  ), [currentIdentity]);

  const isCurrent = useCallback((generation, issueId) => (
    isIdentityCurrent(generation) &&
    (issueId === undefined || Number(issueId) === Number(selectedIssueIdRef.current))
  ), [isIdentityCurrent]);

  const loadUnreadCount = useCallback(async () => {
    if (!companyId || !userId) {
      setUnreadCount(0);
      return 0;
    }
    const generation = generationRef.current;
    const requestId = ++unreadSequenceRef.current;
    const data = await getSupportUnreadCount();
    if (!isCurrent(generation) || requestId !== unreadSequenceRef.current) return null;
    const count = Number(data?.count) || 0;
    setUnreadCount(count);
    return count;
  }, [companyId, isCurrent, userId]);

  const loadIssues = useCallback(async ({ append = false, cursor, filters, silent = false } = {}) => {
    if (!companyId || !userId) return null;
    if (silent && listForegroundRef.current !== null) return null;
    const generation = generationRef.current;
    filtersRef.current = { limit: 20, ...(filters || filtersRef.current) };
    hasLoadedListRef.current = true;
    const requestId = ++listSequenceRef.current;
    if (!silent) {
      listForegroundRef.current = requestId;
      setListState({ status: append ? "loading-more" : "loading", error: null });
    }

    try {
      const data = await listSupportIssues({
        ...filtersRef.current,
        ...(append && cursor ? { cursor } : {}),
      });
      if (!isCurrent(generation) || requestId !== listSequenceRef.current) return null;
      const nextIssues = Array.isArray(data?.issues) ? data.issues : [];
      setIssues((current) => append ? mergeById(current, nextIssues) : nextIssues);
      setIssuesCursor(data?.nextCursor || null);
      if (!silent) setListState({ status: "ready", error: null });
      return data;
    } catch (error) {
      if (!isCurrent(generation) || requestId !== listSequenceRef.current) return null;
      if (silent) return null;
      setListState({ status: "error", error });
      throw error;
    } finally {
      if (!silent && listForegroundRef.current === requestId) {
        listForegroundRef.current = null;
      }
    }
  }, [companyId, isCurrent, userId]);

  const loadCompanies = useCallback(async ({ append = false, cursor, limit = 20 } = {}) => {
    if (!companyId || !userId) return null;
    const generation = generationRef.current;
    const requestId = ++companiesSequenceRef.current;
    setCompaniesState({ status: append ? "loading-more" : "loading", error: null });

    try {
      const data = await listSupportCompanies({
        limit,
        ...(append && cursor ? { cursor } : {}),
      });
      if (!isCurrent(generation) || requestId !== companiesSequenceRef.current) return null;
      const nextCompanies = Array.isArray(data?.companies) ? data.companies : [];
      setCompanies((current) => append ? mergeById(current, nextCompanies) : nextCompanies);
      setCompaniesCursor(data?.nextCursor || null);
      setCompaniesState({ status: "ready", error: null });
      return data;
    } catch (error) {
      if (!isCurrent(generation) || requestId !== companiesSequenceRef.current) return null;
      setCompaniesState({ status: "error", error });
      throw error;
    }
  }, [companyId, isCurrent, userId]);

  const openIssue = useCallback(async (issueId, { silent = false } = {}) => {
    if (!issueId) return null;
    if (silent && detailForegroundRef.current !== null) return null;
    const numericIssueId = Number(issueId);
    const generation = generationRef.current;
    const requestId = ++detailSequenceRef.current;
    const changedSelection = Number(selectedIssueIdRef.current) !== numericIssueId;
    selectedIssueIdRef.current = numericIssueId;

    if (!silent) {
      detailForegroundRef.current = requestId;
      if (changedSelection) {
        readQueueRef.current = null;
        setSelectedIssue(null);
        setMessages([]);
        setMessagesCursor(null);
      }
      setDetailState({ status: "loading", error: null });
    }

    try {
      const data = await getSupportIssue(numericIssueId, { limit: 30 });
      if (!isCurrent(generation, numericIssueId) || requestId !== detailSequenceRef.current) return null;
      setSelectedIssue(data?.issue || null);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      setMessagesCursor(data?.nextCursor || null);
      setDetailState({ status: "ready", error: null });
      return data;
    } catch (error) {
      if (!isCurrent(generation, numericIssueId) || requestId !== detailSequenceRef.current) return null;
      if (silent) return null;
      setDetailState({ status: "error", error });
      throw error;
    } finally {
      if (!silent && detailForegroundRef.current === requestId) {
        detailForegroundRef.current = null;
      }
    }
  }, [isCurrent]);

  const closeIssue = useCallback(() => {
    detailSequenceRef.current += 1;
    detailForegroundRef.current = null;
    selectedIssueIdRef.current = null;
    readQueueRef.current = null;
    setSelectedIssue(null);
    setMessages([]);
    setMessagesCursor(null);
    setDetailState(initialAsyncState);
  }, []);

  const loadOlderMessages = useCallback(async () => {
    const issueId = selectedIssueIdRef.current;
    const before = messagesCursor;
    if (!issueId || !before) return null;
    const generation = generationRef.current;
    const requestId = ++detailSequenceRef.current;
    detailForegroundRef.current = requestId;
    setDetailState((current) => ({ ...current, status: "loading-more" }));

    try {
      const data = await getSupportIssue(issueId, { before, limit: 30 });
      if (!isCurrent(generation, issueId) || requestId !== detailSequenceRef.current) return null;
      const olderMessages = Array.isArray(data?.messages) ? data.messages : [];
      setSelectedIssue(data?.issue || selectedIssue);
      setMessages((current) => mergeById(current, olderMessages, true));
      setMessagesCursor(data?.nextCursor || null);
      setDetailState({ status: "ready", error: null });
      return data;
    } catch (error) {
      if (!isCurrent(generation, issueId) || requestId !== detailSequenceRef.current) return null;
      setDetailState({ status: "error", error });
      throw error;
    } finally {
      if (detailForegroundRef.current === requestId) {
        detailForegroundRef.current = null;
      }
    }
  }, [isCurrent, messagesCursor, selectedIssue]);

  const submitIssue = useCallback(async (payload) => {
    if (createBusyRef.current) return null;
    const generation = generationRef.current;
    const token = { generation };
    createBusyRef.current = token;
    try {
      const data = await createSupportIssue(payload);
      if (!isIdentityCurrent(generation) || createBusyRef.current !== token) return data;
      const issue = data?.issue;
      if (issue) {
        selectedIssueIdRef.current = issue.id;
        setIssues((current) => mergeById(current, [issue], true));
        setSelectedIssue(issue);
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        setMessagesCursor(data?.nextCursor || null);
        setDetailState({ status: "ready", error: null });
      }
      loadUnreadCount().catch(() => undefined);
      return data;
    } catch (error) {
      if (!isIdentityCurrent(generation) || createBusyRef.current !== token) return null;
      throw error;
    } finally {
      if (createBusyRef.current === token) createBusyRef.current = null;
    }
  }, [isIdentityCurrent, loadUnreadCount]);

  const submitMessage = useCallback(async (payload) => {
    const issueId = selectedIssueIdRef.current;
    if (!issueId || replyBusyRef.current.has(issueId)) return null;
    const generation = generationRef.current;
    const token = { generation, issueId };
    replyBusyRef.current.set(issueId, token);
    try {
      const data = await createSupportIssueMessage(issueId, payload);
      if (!isIdentityCurrent(generation) || replyBusyRef.current.get(issueId) !== token) return null;
      if (isCurrent(generation, issueId)) {
        if (data?.issue) setSelectedIssue(data.issue);
        if (data?.message) setMessages((current) => mergeById(current, [data.message]));
        if (data?.issue) {
          setIssues((current) => mergeById(
            current.filter((issue) => issue.id !== data.issue.id),
            [data.issue],
            true
          ));
        }
      }
      loadUnreadCount().catch(() => undefined);
      return data;
    } catch (error) {
      if (!isIdentityCurrent(generation) || replyBusyRef.current.get(issueId) !== token) return null;
      const code = error?.response?.data?.error;
      if (error?.response?.status === 409 &&
        ["ERR_SUPPORT_REPLY_WAITING_SUPPORT", "ERR_SUPPORT_CLOSED"].includes(code)) {
        const blockReply = (issue) => issue?.id === issueId ? {
          ...issue,
          canReply: false,
          replyBlockedReason: code === "ERR_SUPPORT_CLOSED" ? "closed" : "awaiting_support_reply",
          ...(code === "ERR_SUPPORT_CLOSED" ? { status: "closed" } : {}),
        } : issue;
        setIssues((current) => current.map(blockReply));
        if (isCurrent(generation, issueId)) {
          setSelectedIssue(blockReply);
          openIssue(issueId, { silent: true }).catch(() => undefined);
        }
        loadIssues({ silent: true }).catch(() => undefined);
      }
      throw error;
    } finally {
      if (replyBusyRef.current.get(issueId) === token) replyBusyRef.current.delete(issueId);
    }
  }, [isCurrent, isIdentityCurrent, loadIssues, loadUnreadCount, openIssue]);

  const changeStatus = useCallback(async (status) => {
    const issueId = selectedIssueIdRef.current;
    if (!issueId || statusBusyRef.current.has(issueId)) return null;
    const generation = generationRef.current;
    const token = { generation, issueId };
    statusBusyRef.current.set(issueId, token);
    try {
      const data = await updateSupportIssueStatus(issueId, status);
      if (!isIdentityCurrent(generation) || statusBusyRef.current.get(issueId) !== token) return null;
      if (isCurrent(generation, issueId)) {
        if (data?.issue) setSelectedIssue(data.issue);
        if (data?.message) setMessages((current) => mergeById(current, [data.message]));
        if (data?.issue) {
          setIssues((current) => current.map((issue) => issue.id === data.issue.id ? data.issue : issue));
        }
      }
      return data;
    } catch (error) {
      if (!isIdentityCurrent(generation) || statusBusyRef.current.get(issueId) !== token) return null;
      throw error;
    } finally {
      if (statusBusyRef.current.get(issueId) === token) statusBusyRef.current.delete(issueId);
    }
  }, [isCurrent, isIdentityCurrent]);

  const markRead = useCallback(async (lastReadMessageId) => {
    const issueId = selectedIssueIdRef.current;
    const requestedReadId = Number(lastReadMessageId) || 0;
    if (!issueId || !requestedReadId) return null;
    const generation = generationRef.current;
    const currentReadId = Number(selectedIssueRef.current?.lastReadMessageId) || 0;
    if (requestedReadId <= currentReadId) return null;

    const currentQueue = readQueueRef.current;
    if (
      currentQueue &&
      currentQueue.generation === generation &&
      Number(currentQueue.issueId) === Number(issueId)
    ) {
      currentQueue.queuedReadId = Math.max(currentQueue.queuedReadId, requestedReadId);
      return currentQueue.promise;
    }

    const queue = {
      generation,
      issueId,
      queuedReadId: requestedReadId,
      promise: null,
    };
    readQueueRef.current = queue;

    const runQueue = async () => {
      let lastResponse = null;
      while (isCurrent(generation, issueId)) {
        const knownReadId = Number(selectedIssueRef.current?.lastReadMessageId) || 0;
        const nextReadId = queue.queuedReadId;
        queue.queuedReadId = 0;
        if (!nextReadId || nextReadId <= knownReadId) break;

        lastResponse = await markSupportIssueRead(issueId, nextReadId);
        if (!isCurrent(generation, issueId) || readQueueRef.current !== queue) return null;
        const confirmedReadId = Math.max(
          knownReadId,
          Number(lastResponse?.lastReadMessageId) || nextReadId
        );

        const applyConfirmedRead = (current) => {
          if (!current || Number(current.id) !== Number(issueId)) return current;
          const monotonicReadId = Math.max(Number(current.lastReadMessageId) || 0, confirmedReadId);
          const lastActivityId = Number(current.lastActivityId) || 0;
          return {
            ...current,
            lastReadMessageId: monotonicReadId,
            unread: lastActivityId > 0 && monotonicReadId >= lastActivityId
              ? false
              : current.unread,
          };
        };

        setSelectedIssue((current) => {
          const next = applyConfirmedRead(current);
          selectedIssueRef.current = next;
          return next;
        });
        setIssues((current) => current.map(applyConfirmedRead));
        loadUnreadCount().catch(() => undefined);
      }
      return lastResponse;
    };

    queue.promise = runQueue().finally(() => {
      if (readQueueRef.current === queue) readQueueRef.current = null;
    });
    return queue.promise;
  }, [isCurrent, loadUnreadCount]);

  useEffect(() => {
    listSequenceRef.current += 1;
    listForegroundRef.current = null;
    companiesSequenceRef.current += 1;
    detailSequenceRef.current += 1;
    detailForegroundRef.current = null;
    unreadSequenceRef.current += 1;
    filtersRef.current = { limit: 20 };
    hasLoadedListRef.current = false;
    selectedIssueIdRef.current = null;
    setIssues([]);
    setIssuesCursor(null);
    setListState(initialAsyncState);
    setCompanies([]);
    setCompaniesCursor(null);
    setCompaniesState(initialAsyncState);
    setSelectedIssue(null);
    setMessages([]);
    setMessagesCursor(null);
    setDetailState(initialAsyncState);
    setUnreadCount(0);
  }, [currentIdentity]);

  useEffect(() => {
    if (!companyId || !userId) return undefined;

    const reconcile = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      loadUnreadCount().catch(() => undefined);
      if (hasLoadedListRef.current) {
        loadIssues({ filters: filtersRef.current, silent: true }).catch(() => undefined);
      }
      if (selectedIssueIdRef.current) {
        openIssue(selectedIssueIdRef.current, { silent: true }).catch(() => undefined);
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") reconcile();
    };
    const intervalId = window.setInterval(reconcile, 30000);
    window.addEventListener("focus", reconcile);
    document.addEventListener("visibilitychange", handleVisibility);

    let socket;
    let handleSupportEvent;
    if (socketManager?.getSocket) {
      socket = socketManager.getSocket(companyId);
      handleSupportEvent = (payload) => {
        if (Number(payload?.version) !== 1) return;
        loadUnreadCount().catch(() => undefined);
        if (hasLoadedListRef.current) {
          loadIssues({ filters: filtersRef.current, silent: true }).catch(() => undefined);
        }
        if (
          payload?.type === "changed" &&
          Number(payload?.issueId) === Number(selectedIssueIdRef.current)
        ) {
          openIssue(selectedIssueIdRef.current, { silent: true }).catch(() => undefined);
        }
      };
      socket.on("supportIssue:v1", handleSupportEvent);
      socket.on("ready", reconcile);
    }

    loadUnreadCount().catch(() => undefined);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", reconcile);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (socket) {
        socket.off("supportIssue:v1", handleSupportEvent);
        socket.off("ready", reconcile);
      }
    };
  }, [companyId, loadIssues, loadUnreadCount, openIssue, socketManager, userId]);

  return {
    issues: identityChanged ? [] : issues,
    issuesCursor: identityChanged ? null : issuesCursor,
    listState: identityChanged ? initialAsyncState : listState,
    companies: identityChanged ? [] : companies,
    companiesCursor: identityChanged ? null : companiesCursor,
    companiesState: identityChanged ? initialAsyncState : companiesState,
    selectedIssue: identityChanged ? null : selectedIssue,
    messages: identityChanged ? [] : messages,
    messagesCursor: identityChanged ? null : messagesCursor,
    detailState: identityChanged ? initialAsyncState : detailState,
    unreadCount: identityChanged ? 0 : unreadCount,
    loadIssues,
    loadCompanies,
    loadOlderMessages,
    openIssue,
    closeIssue,
    submitIssue,
    submitMessage,
    changeStatus,
    markRead,
    refreshUnreadCount: loadUnreadCount,
  };
};

export default useSupportIssues;
